import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, FilesIcon, FileTextIcon, FolderIcon, FolderOpenIcon, Link2Icon, SearchIcon, XIcon } from 'lucide-react';
import { documentFolders, loanDocuments } from '../../data/documents';
import { formatDate } from '../../utils/conditions';
import type { Condition } from '../../types/conditions';
interface LinkDocumentsDialogProps {
  condition: Condition | null;
  onClose: () => void;
  onLink: (conditionId: string, documentIds: string[]) => void;
}
export function LinkDocumentsDialog({
  condition,
  onClose,
  onLink
}: LinkDocumentsDialogProps) {
  const [query, setQuery] = useState('');
  const [folderId, setFolderId] = useState<string>('all');
  const [selected, setSelected] = useState<string[]>([]);
  const folderCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: loanDocuments.length
    };
    loanDocuments.forEach(d => {
      counts[d.folderId] = (counts[d.folderId] ?? 0) + 1;
    });
    return counts;
  }, []);
  useEffect(() => {
    setQuery('');
    setFolderId('all');
    setSelected([]);
  }, [condition?.id]);
  useEffect(() => {
    if (!condition) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [condition, onClose]);
  const alreadyLinked = condition?.linkedDocumentIds ?? [];
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return loanDocuments.filter(d => (folderId === 'all' || d.folderId === folderId) && (!q || d.name.toLowerCase().includes(q) || d.type.toLowerCase().includes(q)));
  }, [query, folderId]);
  function toggle(id: string) {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }
  return <AnimatePresence data-id="element-228" data-mp="2462jlfr4k0z0">
      {condition && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-229" data-mp="2tuzubmyy7d2j">
          <motion.div className="absolute inset-0 bg-slate-900/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} aria-hidden="true" data-id="element-230" data-mp="2xw1v488ijx6m" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="link-docs-title" initial={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.16
      }} className="relative flex max-h-[85vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-2xl" data-id="element-231" data-mp="37lcz5ggjmbnn">
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-232" data-mp="3vbyy8i8kklli">
              <div data-id="element-233" data-mp="44d0f68j7dck">
                <h2 id="link-docs-title" className="text-base font-semibold text-slate-900" data-id="element-234" data-mp="43uufhw2hkrg">
                  Link existing documents
                </h2>
                <p className="mt-0.5 text-sm text-slate-500" data-id="element-235" data-mp="1jo3yt1r65xnh">
                  Attach documents already on this loan to <span className="font-medium text-slate-700" data-id="element-236" data-mp="1ex40pt6bd9je">{condition.name}</span>.
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-237" data-mp="19x989l6w4bw8">
                <XIcon className="h-5 w-5" data-id="element-238" data-mp="vzklg7t9iuoj" />
              </button>
            </header>

            <div className="grid min-h-0 flex-1 grid-cols-1 sm:grid-cols-[220px_1fr]" data-id="element-239" data-mp="r081pvxou3q6">
            <nav aria-label="Document folders" className="border-b border-slate-200 bg-slate-50 p-2 sm:border-b-0 sm:border-r sm:overflow-y-auto" data-id="element-240" data-mp="2qd2e298iyhqf">
              <p className="px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-241" data-mp="2ldzayuc3ull3">Folders</p>
              <ul className="flex gap-1 overflow-x-auto sm:block sm:space-y-0.5" data-id="element-242" data-mp="2tcaxqoe9tp69">
                {[{
                id: 'all',
                name: 'All documents'
              }, ...documentFolders].map(f => {
                const active = folderId === f.id;
                const Icon = f.id === 'all' ? FilesIcon : active ? FolderOpenIcon : FolderIcon;
                return <li key={f.id} className="shrink-0" data-id="element-243" data-mp="2ammf9urlw17s">
                      <button type="button" onClick={() => setFolderId(f.id)} aria-current={active ? 'true' : undefined} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${active ? 'bg-white font-semibold text-brand-dark shadow-sm ring-1 ring-slate-200' : 'text-slate-700 hover:bg-white/70'}`} data-id="element-244" data-mp="2fedz7mg9b8oy">
                        <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-brand' : 'text-slate-400'}`} aria-hidden="true" data-id="element-245" data-mp="1d3xpcmk0ysjx" />
                        <span className="flex-1 truncate" data-id="element-246" data-mp="1m0er4z6i1e30">{f.name}</span>
                        <span className="text-xs tabular-nums text-slate-400" data-id="element-247" data-mp="1h24wqqslmhig">{folderCounts[f.id] ?? 0}</span>
                      </button>
                    </li>;
              })}
              </ul>
            </nav>

            <div className="flex min-h-0 flex-col" data-id="element-248" data-mp="24t94xpsdz05n">
            <div className="border-b border-slate-200 px-5 py-3" data-id="element-249" data-mp="24rz8f516m014">
              <div className="relative" data-id="element-250" data-mp="20r5jq9fx0h76">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-251" data-mp="20s72z17349zz" />
                <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={folderId === 'all' ? 'Search all documents' : `Search in ${documentFolders.find(f => f.id === folderId)?.name ?? 'folder'}`} aria-label="Search documents" autoFocus className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" data-id="element-252" data-mp="29slu4h6qcjbe" />
              </div>
            </div>

            <div className="min-h-[280px] flex-1 overflow-y-auto" data-id="element-253" data-mp="1z76iiuqs25yg">
              {results.length === 0 ? <div className="flex flex-col items-center px-5 py-12 text-center" data-id="element-254" data-mp="243wzdkaep5kx">
                  <FolderIcon className="h-8 w-8 text-slate-300" aria-hidden="true" data-id="element-255" data-mp="4svqkdevyvog" />
                  <p className="mt-2 text-sm font-medium text-slate-700" data-id="element-256" data-mp="9qnllgatuxwv">
                    {query ? 'No documents match your search' : 'This folder is empty'}
                  </p>
                </div> : <ul role="listbox" aria-multiselectable="true" aria-label="Loan documents" className="divide-y divide-slate-100" data-id="element-257" data-mp="3tlk31qzavqm1">
                  {results.map(d => {
                  const linked = alreadyLinked.includes(d.id);
                  const isSelected = selected.includes(d.id);
                  return <li key={d.id} data-id="element-258" data-mp="l4fs42xeyjqj">
                        <button type="button" role="option" aria-selected={isSelected} disabled={linked} onClick={() => toggle(d.id)} className={`flex w-full items-center gap-3 px-5 py-3 text-left transition-colors focus:outline-none focus-visible:bg-slate-50 disabled:cursor-default ${isSelected ? 'bg-brand/[0.05]' : 'hover:bg-slate-50'}`} data-id="element-259" data-mp="g6xl4i2yiel6">
                          <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isSelected || linked ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white'} ${linked ? 'opacity-50' : ''}`} aria-hidden="true" data-id="element-260" data-mp="zvf6wjbfnqqh">
                            {(isSelected || linked) && <CheckIcon className="h-3 w-3" data-id="element-261" data-mp="1inr996236s0m" />}
                          </span>
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-red-50" data-id="element-262" data-mp="14sp7mv49votm">
                            <FileTextIcon className="h-4 w-4 text-red-600" aria-hidden="true" data-id="element-263" data-mp="1xj761iu1x20p" />
                          </span>
                          <span className="min-w-0 flex-1" data-id="element-264" data-mp="22a5u54la74ei">
                            <span className="block truncate text-sm font-medium text-slate-900" data-id="element-265" data-mp="2bopp144kjfbr">{d.name}</span>
                            <span className="block truncate text-xs text-slate-500" data-id="element-266" data-mp="27ndv4menw7xr">
                              {d.type} · {d.pages} {d.pages === 1 ? 'page' : 'pages'} · {d.uploadedBy}, {formatDate(d.uploadedAt)}
                            </span>
                          </span>
                          <span className="hidden shrink-0 flex-col items-end gap-1 sm:flex" data-id="element-267" data-mp="2ghsb6r70nmhx">
                            {folderId === 'all' && <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600" data-id="element-268" data-mp="1opbcqqgmd98x">
                                <FolderIcon className="h-3 w-3" aria-hidden="true" data-id="element-269" data-mp="1xoy40jfmwyo9" />
                                {documentFolders.find(f => f.id === d.folderId)?.name}
                              </span>}
                            {linked ? <span className="text-[11px] font-semibold text-brand" data-id="element-270" data-mp="3967n34uupop1">Already linked</span> : d.attachedTo ? <span className="text-[11px] text-slate-500" data-id="element-271" data-mp="2ft2ale018i3y">Also on: {d.attachedTo}</span> : null}
                          </span>
                        </button>
                      </li>;
                })}
                </ul>}
            </div>
            </div>
            </div>

            <footer className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-3" data-id="element-272" data-mp="1x2u910bzdzpv">
              <p className="text-sm text-slate-500" data-id="element-273" data-mp="1s3hm6fl40sna">
                {selected.length === 0 ? 'Select one or more documents' : `${selected.length} selected`}
              </p>
              <div className="flex gap-2" data-id="element-274" data-mp="1n70zzqxh0ocu">
                <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-275" data-mp="3mi0repx1da90">
                  Cancel
                </button>
                <button type="button" disabled={selected.length === 0} onClick={() => {
              onLink(condition.id, selected);
              onClose();
            }} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-276" data-mp="2mm3kps6smy7">
                  <Link2Icon className="h-4 w-4" aria-hidden="true" data-id="element-277" data-mp="3l26hbe2gqrj1" />
                  Link {selected.length > 0 ? selected.length : ''} {selected.length === 1 ? 'document' : 'documents'}
                </button>
              </div>
            </footer>
          </motion.div>
        </div>}
    </AnimatePresence>;
}